
import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { UserRole } from '../../types';

const VideoIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>;
const UsersIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21a6 6 0 00-9-5.197M15 21a6 6 0 00-9-5.197" /></svg>;
const FilterIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L16 11.414V16l-4 2v-6.586L3.293 6.707A1 1 0 013 6V4z" /></svg>;
const CompanyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>;
const KpiIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>;
const TenantIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;

const NavItem: React.FC<{ to: string; icon: React.ReactElement; children: React.ReactNode }> = ({ to, icon, children }) => {
    return (
        <NavLink
            to={to}
            className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-lg transition-colors duration-200 ${isActive ? 'brand-bg text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`
            }
        >
            {icon}
            <span className="ml-4 font-medium">{children}</span>
        </NavLink>
    );
};

const Sidebar: React.FC = () => {
  const authContext = useContext(AuthContext);
  const userRole = authContext?.user?.role;
  const tenant = authContext?.tenant;
  const isPlatformOwner = userRole === UserRole.PLATFORM_OWNER;

  return (
    <div className="w-64 bg-gray-900 flex-shrink-0 flex flex-col border-r border-gray-800">
      <div className="h-20 flex items-center justify-center px-6 border-b border-gray-800/50">
        {(!isPlatformOwner && tenant?.settings.branding.logoUrl) ? (
            <img src={tenant.settings.branding.logoUrl} alt={tenant.name} className="max-h-12 max-w-full object-contain" />
        ) : (
            <div className="text-xl font-bold text-white tracking-wider">
                <span className="brand-text">M</span>ystery<span className="brand-text">S</span>hopper<span className="brand-text">IA</span>
            </div>
        )}
      </div>
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto custom-scrollbar">
        {isPlatformOwner && (
            <>
              <NavItem to="/tenants" icon={<TenantIcon />}>Agencias (Tenants)</NavItem>
              <NavItem to="/platform-users" icon={<UsersIcon />}>Usuarios Globales</NavItem>
            </>
        )}
        
        <div className="pt-2 pb-1 px-4">
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Operaciones</p>
        </div>
        <NavItem to="/videos" icon={<VideoIcon />}>Videos</NavItem>
        <NavItem to="/companies" icon={<CompanyIcon />}>Clientes (Compañías)</NavItem>
        
        <div className="pt-4 pb-1 px-4">
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Configuración</p>
        </div>
        {!isPlatformOwner && <NavItem to="/users" icon={<UsersIcon />}>Equipo / Usuarios</NavItem>}
        <NavItem to="/filters" icon={<FilterIcon />}>Filtros</NavItem>
        <NavItem to="/kpis" icon={<KpiIcon />}>KPIs</NavItem>
      </nav>
      
      <div className="p-4 border-t border-gray-800 bg-gray-950/30">
        <p className="text-[10px] text-gray-600 uppercase font-bold tracking-widest mb-1">Entorno:</p>
        <p className="text-xs text-white font-semibold truncate">{isPlatformOwner ? 'PLATAFORMA GLOBAL' : tenant?.name}</p>
        <p className="text-[10px] text-gray-600 mt-4">&copy; 2024 MysteryShopperIA</p>
      </div>
    </div>
  );
};

export default Sidebar;